import * as Sentry from '@sentry/vue'
import { pad } from '@/lib/helpers/numeric'

/**
 * Convert a Date to datetime-local input format
 */
export const toDatetimeLocal = (date) => {
    if (typeof date === 'string') {
        date = new Date(date)
    }
    if (!(date instanceof Date && !isNaN(date))) {
        return ''
    }

    const pad4 = (i) => (i < 10 ? '000' : i < 100 ? '00' : i < 1000 ? '0' : '') + i

    const YYYY = pad4(date.getFullYear())
    const MM = pad(date.getMonth() + 1)
    const DD = pad(date.getDate())
    const HH = pad(date.getHours())
    const II = pad(date.getMinutes())
    const SS = pad(date.getSeconds())

    return `${YYYY}-${MM}-${DD}T${HH}:${II}:${SS}`
}

/**
 * Convert datetime-local format to ISO string
 */
export const fromDatetimeLocal = (date) => {
    if (typeof date === 'string') {
        date = new Date(date)
    }
    return date instanceof Date && !isNaN(date) ? new Date(date).toISOString() : ''
}

/**
 * Transform seconds into hours, minutes and seconds
 *
 * @param {number} seconds - Seconds to format
 * @param {number|boolean} maxUnits - Number of maximum units (1, 2, or 3)
 * @param {boolean} returnString - If true, return a string "{value} {unit}"
 * @returns {object|string}
 */
export const secondsToHMS = (seconds, maxUnits = 3, returnString = false) => {
    const emptyResult = returnString ? '' : { value: '', unit: '' }

    if (isNaN(seconds)) return emptyResult

    if (seconds >= 86400) {
        const warningMessage = 'We do not manage as much seconds for now ' + seconds
        console.warn(warningMessage)
        Sentry.captureMessage(warningMessage)
        return returnString ? '01 d' : { value: '01', unit: 'd' }
    }

    const arrTime = new Date(seconds * 1000).toISOString().substr(11, 8).split(':')
    let time = {}

    if (!maxUnits) {
        time = arrTime[0] === '00' ? arrTime.slice(1).join(':') : arrTime.join(':')
    } else {
        while (arrTime[0] === '00') {
            arrTime.shift()
        }

        if (arrTime.length === 0) {
            return returnString ? '00 sec' : { value: '00', unit: 'sec' }
        }

        switch (arrTime.length) {
            case 1:
                time.unit = 'sec'
                break
            case 2:
                if (maxUnits === 1) {
                    if (arrTime[1] >= 30) {
                        if (arrTime[0] < 59) {
                            arrTime[0]++
                            if (arrTime[0].toString().length === 1) arrTime[0] = '0' + arrTime[0].toString()
                        } else {
                            return returnString ? '01 h' : { value: '01', unit: 'h' }
                        }
                    }
                    arrTime.pop()
                }
                time.unit = 'min'
                break
            default:
                if (maxUnits < 3) {
                    if (maxUnits === 2) {
                        if (arrTime[2] >= 30) {
                            arrTime[1]++
                            if (arrTime[1].toString().length === 1) arrTime[1] = '0' + arrTime[1].toString()
                        }
                        arrTime.pop()
                    } else {
                        if (arrTime[1] >= 30) {
                            arrTime[0]++
                            if (arrTime[0].toString().length === 1) arrTime[0] = '0' + arrTime[0].toString()
                        }
                        arrTime.pop()
                        arrTime.pop()
                    }
                }
                time.unit = 'h'
        }

        time.value = arrTime.join(':')
    }

    return returnString ? time.value + ' ' + time.unit : time
}

export const formatApproximateDuration = (
    seconds,
    {
        compact = false,
        labels = {
            hour: 'h',
            minute: 'min',
            second: 's',
        },
    } = {}
) => {
    const normalizedSeconds = Math.max(0, Math.round(Number(seconds) || 0))
    const hours = Math.floor(normalizedSeconds / 3600)
    const minutes = Math.floor((normalizedSeconds % 3600) / 60)
    const remainingSeconds = normalizedSeconds % 60
    const parts = []

    if (hours > 0) {
        parts.push(`${hours}${compact ? '' : ' '}${labels.hour}`)
    }

    if (minutes > 0 || hours > 0) {
        parts.push(`${minutes}${compact ? '' : ' '}${labels.minute}`)
    }

    if (hours === 0 && (remainingSeconds > 0 || parts.length === 0)) {
        parts.push(`${remainingSeconds}${compact ? '' : ' '}${labels.second}`)
    }

    return `~${parts.join(' ')}`
}

/**
 * Transform seconds into H:M:S.milliseconds format
 *
 * @param {number} seconds - Seconds to format (can be float)
 * @param {number|boolean} maxUnits - Max number of time units: 1, 2, or 3
 * @param {boolean} returnString - If true, returns a formatted string "value unit"
 * @returns {object|string}
 */
export const secondsToHMSM = (seconds, maxUnits = 3, returnString = false) => {
    if (isNaN(seconds)) return returnString ? '' : { value: '', unit: '' }

    if (seconds >= 86400) {
        const warningMessage = 'We do not manage as much seconds for now ' + seconds
        console.warn(warningMessage)
        return returnString ? '01 d' : { value: '01', unit: 'd' }
    }

    const totalMs = Math.round(seconds * 1000)
    const date = new Date(totalMs)
    const arrTime = date.toISOString().substr(11, 8).split(':')
    const ms = String(totalMs % 1000).padStart(3, '0')

    let time = {}

    while (arrTime[0] === '00') {
        arrTime.shift()
    }

    if (arrTime.length === 0) {
        return returnString ? '00.000 sec' : { value: '00.000', unit: 'sec' }
    }

    switch (arrTime.length) {
        case 1:
            time.unit = 'sec'
            break
        case 2:
            if (maxUnits === 1) {
                if (parseInt(arrTime[1]) >= 30) {
                    arrTime[0] = pad(parseInt(arrTime[0]) + 1)
                }
                arrTime.pop()
            }
            time.unit = 'min'
            break
        default:
            if (maxUnits < 3) {
                if (maxUnits === 2) {
                    if (parseInt(arrTime[2]) >= 30) {
                        arrTime[1] = pad(parseInt(arrTime[1]) + 1)
                    }
                    arrTime.pop()
                } else {
                    if (parseInt(arrTime[1]) >= 30) {
                        arrTime[0] = pad(parseInt(arrTime[0]) + 1)
                    }
                    arrTime.pop()
                    arrTime.pop()
                }
            }
            time.unit = 'h'
    }

    const lastIndex = arrTime.length - 1
    arrTime[lastIndex] = arrTime[lastIndex] + '.' + ms
    time.value = arrTime.join(':')

    return returnString ? `${time.value} ${time.unit}` : time
}

/**
 * Transform time string or object to seconds
 *
 * @param {string|object} time - Time in string format "HH:MM(:SS) sec|m|h" or object
 * @param {boolean} isString - If true, time is a string
 * @returns {number}
 */
export const HMStoSeconds = (time, isString = true) => {
    let seconds = 0

    if (isString) {
        const [value, unit] = time.split(' ')
        time = { value, unit }
    }

    switch (time.unit) {
        case 'sec':
            seconds = time.value
            break
        case 'min':
            seconds = parseInt(time.value.split(':')[1]) + parseInt(time.value.split(':')[0]) * 60
            break
        case 'h':
            seconds =
                parseInt(time.value.split(':')[2]) +
                parseInt(time.value.split(':')[1]) * 60 +
                parseInt(time.value.split(':')[0]) * 3600
            break
    }

    return seconds
}
